/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable typescript-sort-keys/interface */
/* eslint-disable react/jsx-no-literals */
// components/BudgetPanel/BudgetPanelContent.tsx
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import styled from 'styled-components';

import { getExchangeRates } from '@/features/canvas/api/exchange-rates';
import {
  getBudgetBySection,
  getBudgetByTimePeriod,
  getRoadmapBudgetSummary,
} from '@/features/canvas/api/initiative_budgets';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';
import {
  BudgetSummary,
  FilterBudget,
  SectionBudget,
  TimePeriodBudget,
} from '@/features/canvas/types/budget-type';
import { formatCurrencyParts } from '@/features/canvas/utils/currency';

import { CapexOpexView } from './CapexOpexView';
import { CurrencySelector } from './CurrencySelector';
import { SpendView } from './SpendView';
import { TotalBudgetView } from './TotalBudgetView';

// Helper function to convert a single numeric value
const convertAmount = (
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  exchangeRates: Record<string, number> | undefined
): number => {
  if (!exchangeRates || fromCurrency === toCurrency) return amount;
  const conversionRate = exchangeRates[toCurrency] / exchangeRates[fromCurrency];
  return Math.round(amount * conversionRate);
};

// Convert budget summary
const convertBudgetSummary = (
  summary: BudgetSummary,
  fromCurrency: string,
  toCurrency: string,
  exchangeRates: Record<string, number> | undefined
): BudgetSummary => {
  if (!exchangeRates || fromCurrency === toCurrency) return summary;

  return {
    ...summary,
    currency: toCurrency,
    total_budget: convertAmount(summary.total_budget, fromCurrency, toCurrency, exchangeRates),
    total_capex: convertAmount(summary.total_capex, fromCurrency, toCurrency, exchangeRates),
    total_opex: convertAmount(summary.total_opex, fromCurrency, toCurrency, exchangeRates),
    total_spend: convertAmount(summary.total_spend, fromCurrency, toCurrency, exchangeRates),
  };
};

// Convert time period budget
const convertTimePeriodBudget = (
  period: TimePeriodBudget,
  fromCurrency: string,
  toCurrency: string,
  exchangeRates: Record<string, number> | undefined
): TimePeriodBudget => {
  if (!exchangeRates || fromCurrency === toCurrency) return period;

  return {
    ...period,
    total_budget: convertAmount(period.total_budget, fromCurrency, toCurrency, exchangeRates),
    capex: convertAmount(period.capex, fromCurrency, toCurrency, exchangeRates),
    opex: convertAmount(period.opex, fromCurrency, toCurrency, exchangeRates),
    spend: convertAmount(period.spend, fromCurrency, toCurrency, exchangeRates),
  };
};

// Convert section budget
const convertSectionBudget = (
  section: SectionBudget,
  fromCurrency: string,
  toCurrency: string,
  exchangeRates: Record<string, number> | undefined
): SectionBudget => {
  if (!exchangeRates || fromCurrency === toCurrency) return section;

  return {
    ...section,
    total_budget: convertAmount(section.total_budget, fromCurrency, toCurrency, exchangeRates),
    capex: convertAmount(section.capex, fromCurrency, toCurrency, exchangeRates),
    opex: convertAmount(section.opex, fromCurrency, toCurrency, exchangeRates),
    spend: convertAmount(section.spend, fromCurrency, toCurrency, exchangeRates),
  };
};

type BudgetFieldType = {
  type: string;
  value?: string | number;
  [key: string]: any;
};

// Helper function to extract budget value from a node
const getBudgetValue = (node: any, fieldType: string): number => {
  // Get budget from the correct location
  const budget = node.initiatives?.budget;

  if (!budget) return 0;

  // If budget has fields array
  if (budget.fields && Array.isArray(budget.fields)) {
    const field = budget.fields.find((f: BudgetFieldType) => f.type === fieldType);
    return parseFloat(String(field?.value || '0'));
  }

  // If budget has direct properties
  if (fieldType === 'total' && budget.total_budget !== undefined) {
    return parseFloat(String(budget.total_budget || '0'));
  }
  if (fieldType === 'capex' && budget.capex !== undefined) {
    return parseFloat(String(budget.capex || '0'));
  }
  if (fieldType === 'opex' && budget.opex !== undefined) {
    return parseFloat(String(budget.opex || '0'));
  }
  if (fieldType === 'spend' && budget.spend !== undefined) {
    return parseFloat(String(budget.spend || '0'));
  }

  return 0;
};

export const BudgetPanelContent = () => {
  const { t } = useTranslation();
  const {
    budgetViewTab,
    roadmap,
    filteredNodes,
    isPublicView,
    sortedTimePeriods,
    sortedVerticalSections,
    sortedHorizontalSections,
    toggledBudgetNodeIds,
    activeFilterIds,
    optionCheckedIds,
    filters,
    isMultiFilterEnabled,
    initiativeBudgets,
    activeDependencyNodeIds,
    reorderedFilterOptions,
    showDependencies,
  } = useEditorContext();
  const [selectedCurrency, setSelectedCurrency] = useState<string>('GBP');

  // Dynamic base currency - determine from initiative budgets data
  const baseCurrency = useMemo(() => {
    // If we have initiative budgets data, use the most common currency
    if (initiativeBudgets && initiativeBudgets.size > 0) {
      const currencyCounts = new Map<string, number>();

      Array.from(initiativeBudgets.values()).forEach(budgetData => {
        const currency = budgetData.currency || 'GBP';
        currencyCounts.set(currency, (currencyCounts.get(currency) || 0) + 1);
      });

      // Return the most common currency, defaulting to GBP if tied
      const mostCommon = Array.from(currencyCounts.entries()).sort((a, b) => b[1] - a[1])[0];

      return mostCommon ? mostCommon[0] : 'GBP';
    }

    // Default to GBP if no budget data available
    return 'GBP';
  }, [initiativeBudgets]);

  // Determine the default breakdown view based on active filters
  // Memoize this function with useCallback to avoid recreating it on every render
  const getDefaultBreakdownView = useCallback((): string => {
    // If in multiple filter mode, default to time
    if (isMultiFilterEnabled) {
      return 'timePeriod';
    }

    // If there's exactly one active filter in single mode
    if (activeFilterIds.length === 1 && !isMultiFilterEnabled) {
      const activeFilterId = activeFilterIds[0];

      // Check if the filter exists
      const activeFilter = filters?.find(f => f.id === activeFilterId);

      if (activeFilter) {
        const result = `filter-${activeFilterId}`;
        return result;
      }
    }

    // Default to time period
    return 'timePeriod';
  }, [activeFilterIds, filters, isMultiFilterEnabled]);

  const [breakdownView, setBreakdownView] = useState<'section' | 'timePeriod' | string>(
    getDefaultBreakdownView()
  );

  // Update breakdown view when active filters change or when switching to budget mode
  useEffect(() => {
    const newDefaultView = getDefaultBreakdownView();
    setBreakdownView(newDefaultView);
  }, [activeFilterIds, isMultiFilterEnabled, filters, getDefaultBreakdownView]);

  // Get only visible node IDs
  const visibleNodeIds = useMemo(() => {
    // Get nodes that match the current filters
    const nodesMatchingFilters = filteredNodes.filter(node => {
      // First check if node is toggled off in budget view
      if (toggledBudgetNodeIds.has(node.id!)) {
        return false;
      }

      // Check if dependencies are turned on
      if (showDependencies) {
        // If dependencies are on but no specific chain is selected (default state)
        if (activeDependencyNodeIds.size === 0) {
          // Check if node has dependencies or is part of any dependency relationship
          const allNodeIdsInDependencyRelations = filteredNodes.reduce((acc, n) => {
            const nodeIds = [...acc];
            const slaveNodeIds = n.dependencies?.map(dep => dep.slaveNodeId) || [];
            const masterNodeIds = n.dependencies?.map(dep => dep.masterNodeId) || [];
            return [...nodeIds, ...slaveNodeIds, ...masterNodeIds];
          }, [] as number[]);

          const hasNoDependencyAndIsNotDependency =
            !allNodeIdsInDependencyRelations.includes(node.id!) && !node.dependencies?.length;

          // Exclude orphaned nodes (nodes with no dependencies)
          if (hasNoDependencyAndIsNotDependency) {
            return false;
          }

          // Include all nodes that are part of dependency relationships
          return node.isVisible;
        }
        // When a specific dependency chain is active, only include nodes in that chain
        return node.isVisible && activeDependencyNodeIds.has(node.id!);
      }

      // If no filters are active and no dependencies selected, include all visible nodes
      if (activeFilterIds.length === 0) {
        return node.isVisible;
      }

      // Check if node matches active filters
      const activeFilters =
        filters?.filter(f => f.id !== undefined && activeFilterIds.includes(f.id!)) || [];

      // For multi-filter mode
      if (isMultiFilterEnabled) {
        // Node must match ALL active filters
        return (
          activeFilters.every(filter =>
            filter.nodes?.some(
              n => n.nodeId === node.id && optionCheckedIds.includes(n.optionId ?? -1)
            )
          ) && node.isVisible
        );
      }

      // For single filter mode
      const doesNodeHaveActiveFilter = activeFilters.some(filter =>
        filter.nodes?.some(n => n.nodeId === node.id)
      );

      const isNodesOptionChecked = activeFilters.every(filter =>
        filter.selection === 'multiple'
          ? filter.nodes?.some(
              n => n.nodeId === node.id && optionCheckedIds.includes(n.optionId ?? -1)
            )
          : optionCheckedIds.includes(filter.nodes?.find(n => n.nodeId === node.id)?.optionId ?? -1)
      );

      const isNotInFilter =
        (doesNodeHaveActiveFilter && optionCheckedIds.length > 0 && !isNodesOptionChecked) ||
        (!doesNodeHaveActiveFilter && activeFilterIds.length > 0);

      // Include node only if it's not filtered out
      return node.isVisible && !isNotInFilter;
    });

    return nodesMatchingFilters.map(node => node.id).filter(Boolean) as number[];
  }, [
    filteredNodes,
    toggledBudgetNodeIds,
    activeFilterIds,
    optionCheckedIds,
    filters,
    isMultiFilterEnabled,
    activeDependencyNodeIds,
    showDependencies,
  ]);

  // Fetch exchange rates - enable even in public view if you want currency conversion
  const { data: exchangeRatesData } = useQuery(['exchange-rates'], getExchangeRates, {
    staleTime: 60 * 60 * 1000, // 1 hour
  });

  // Convert to Record<string, number> format
  const exchangeRates = exchangeRatesData as Record<string, number> | undefined;

  // For public view, calculate budget summary from nodes data
  const calculateBudgetSummaryFromNodes = useMemo(() => {
    if (!isPublicView || visibleNodeIds.length === 0) return undefined;

    // Calculate totals from visible nodes
    const summary: BudgetSummary = {
      currency: baseCurrency,
      total_budget: 0,
      total_capex: 0,
      total_opex: 0,
      total_spend: 0,
      initiative_count: 0,
    };

    filteredNodes.forEach(node => {
      if (!node.isVisible || toggledBudgetNodeIds.has(node.id!)) return;
      if (!visibleNodeIds.includes(node.id!)) return;

      // Use helper function to get budget values
      const totalBudget = getBudgetValue(node, 'total');
      const capex = getBudgetValue(node, 'capex');
      const opex = getBudgetValue(node, 'opex');
      const spend = getBudgetValue(node, 'spend');

      // Also check initiativeBudgets map if available
      if (node.initiatives?.id && initiativeBudgets.has(node.initiatives.id)) {
        const budgetData = initiativeBudgets.get(node.initiatives.id);
        if (budgetData) {
          // Convert to base currency before adding
          const nodeCurrency = budgetData.currency || baseCurrency;

          summary.total_budget += convertAmount(
            budgetData.total_budget || 0,
            nodeCurrency,
            baseCurrency,
            exchangeRates
          );
          summary.total_capex += convertAmount(
            budgetData.capex || 0,
            nodeCurrency,
            baseCurrency,
            exchangeRates
          );
          summary.total_opex += convertAmount(
            budgetData.opex || 0,
            nodeCurrency,
            baseCurrency,
            exchangeRates
          );
          summary.total_spend += convertAmount(
            budgetData.spend || 0,
            nodeCurrency,
            baseCurrency,
            exchangeRates
          );
          summary.initiative_count += 1;
          return;
        }
      }

      summary.total_budget += totalBudget;
      summary.total_capex += capex;
      summary.total_opex += opex;
      summary.total_spend += spend;
      if (totalBudget > 0) {
        summary.initiative_count += 1;
      }
    });

    return summary;
  }, [
    isPublicView,
    visibleNodeIds,
    filteredNodes,
    baseCurrency,
    toggledBudgetNodeIds,
    initiativeBudgets,
    exchangeRates,
  ]);

  // Calculate time period breakdown for public view
  const calculateTimePeriodBreakdown = useMemo(() => {
    if (!isPublicView || visibleNodeIds.length === 0) return [];

    const breakdownMap = new Map<number, TimePeriodBudget>();

    // Initialize with time periods
    sortedTimePeriods.forEach(tp => {
      breakdownMap.set(tp.id!, {
        time_period_id: tp.id!,
        time_period: tp.title,
        total_budget: 0,
        capex: 0,
        opex: 0,
        spend: 0,
        initiative_count: 0,
      });
    });

    // Sum up budgets by time period
    filteredNodes.forEach(node => {
      if (!node.isVisible || !node.timePeriodId || toggledBudgetNodeIds.has(node.id!)) return;
      if (!visibleNodeIds.includes(node.id!)) return;

      const breakdown = breakdownMap.get(node.timePeriodId);
      if (breakdown) {
        // Check initiativeBudgets map first
        if (node.initiatives?.id && initiativeBudgets.has(node.initiatives.id)) {
          const budgetData = initiativeBudgets.get(node.initiatives.id);
          if (budgetData) {
            // Convert to base currency before adding
            const nodeCurrency = budgetData.currency || baseCurrency;

            breakdown.total_budget += convertAmount(
              budgetData.total_budget || 0,
              nodeCurrency,
              baseCurrency,
              exchangeRates
            );
            breakdown.capex += convertAmount(
              budgetData.capex || 0,
              nodeCurrency,
              baseCurrency,
              exchangeRates
            );
            breakdown.opex += convertAmount(
              budgetData.opex || 0,
              nodeCurrency,
              baseCurrency,
              exchangeRates
            );
            breakdown.spend += convertAmount(
              budgetData.spend || 0,
              nodeCurrency,
              baseCurrency,
              exchangeRates
            );
            breakdown.initiative_count += 1;
            return;
          }
        }

        // Otherwise use helper function
        breakdown.total_budget += getBudgetValue(node, 'total');
        breakdown.capex += getBudgetValue(node, 'capex');
        breakdown.opex += getBudgetValue(node, 'opex');
        breakdown.spend += getBudgetValue(node, 'spend');
        if (getBudgetValue(node, 'total') > 0) {
          breakdown.initiative_count += 1;
        }
      }
    });

    return Array.from(breakdownMap.values());
  }, [
    isPublicView,
    visibleNodeIds,
    filteredNodes,
    sortedTimePeriods,
    toggledBudgetNodeIds,
    initiativeBudgets,
    baseCurrency,
    exchangeRates,
  ]);

  // Calculate section breakdown for public view
  const calculateSectionBreakdown = useMemo(() => {
    if (!isPublicView || visibleNodeIds.length === 0) return [];

    const breakdownMap = new Map<number, SectionBudget>();

    // Initialize with sections
    [...sortedVerticalSections, ...sortedHorizontalSections].forEach(section => {
      breakdownMap.set(section.id!, {
        section_id: section.id!,
        section: section.title || '',
        total_budget: 0,
        capex: 0,
        opex: 0,
        spend: 0,
        initiative_count: 0,
      });
    });

    // Sum up budgets by section
    filteredNodes.forEach(node => {
      if (!node.isVisible || !node.sectionId || toggledBudgetNodeIds.has(node.id!)) return;
      if (!visibleNodeIds.includes(node.id!)) return;

      const breakdown = breakdownMap.get(node.sectionId);
      if (breakdown) {
        // Check initiativeBudgets map first
        if (node.initiatives?.id && initiativeBudgets.has(node.initiatives.id)) {
          const budgetData = initiativeBudgets.get(node.initiatives.id);
          if (budgetData) {
            // Convert to base currency before adding
            const nodeCurrency = budgetData.currency || baseCurrency;

            breakdown.total_budget += convertAmount(
              budgetData.total_budget || 0,
              nodeCurrency,
              baseCurrency,
              exchangeRates
            );
            breakdown.capex += convertAmount(
              budgetData.capex || 0,
              nodeCurrency,
              baseCurrency,
              exchangeRates
            );
            breakdown.opex += convertAmount(
              budgetData.opex || 0,
              nodeCurrency,
              baseCurrency,
              exchangeRates
            );
            breakdown.spend += convertAmount(
              budgetData.spend || 0,
              nodeCurrency,
              baseCurrency,
              exchangeRates
            );
            breakdown.initiative_count += 1;
            return;
          }
        }

        // Otherwise use helper function
        breakdown.total_budget += getBudgetValue(node, 'total');
        breakdown.capex += getBudgetValue(node, 'capex');
        breakdown.opex += getBudgetValue(node, 'opex');
        breakdown.spend += getBudgetValue(node, 'spend');
        if (getBudgetValue(node, 'total') > 0) {
          breakdown.initiative_count += 1;
        }
      }
    });

    return Array.from(breakdownMap.values());
  }, [
    isPublicView,
    visibleNodeIds,
    filteredNodes,
    sortedVerticalSections,
    sortedHorizontalSections,
    toggledBudgetNodeIds,
    initiativeBudgets,
    baseCurrency,
    exchangeRates,
  ]);

  // Calculate filter-based breakdown - FIXED VERSION
  const calculateFilterBreakdown = useMemo(() => {
    const breakdowns: Record<number, FilterBudget[]> = {};

    activeFilterIds.forEach(filterId => {
      const filter = filters?.find(f => f.id === filterId);
      if (!filter) return;

      // Check for reordered options first, fallback to original options
      const options = reorderedFilterOptions[filterId] || filter.options;

      const filterBreakdown: FilterBudget[] = [];

      // Initialize breakdown for each option (using the potentially reordered options)
      options.forEach(option => {
        filterBreakdown.push({
          filter_option_id: option.id!,
          filter_option_title: option.title || '',
          total_budget: 0,
          capex: 0,
          opex: 0,
          spend: 0,
          initiative_count: 0,
        });
      });

      // Calculate budgets for each option
      filteredNodes.forEach(node => {
        // Skip if node is not visible
        if (!visibleNodeIds.includes(node.id!)) return;

        const filterNode = filter.nodes?.find(n => n.nodeId === node.id);
        if (!filterNode) return;

        // Find the option breakdown for this node's option
        const optionBreakdown = filterBreakdown.find(
          fb => fb.filter_option_id === filterNode.optionId
        );
        if (!optionBreakdown) return;

        // Check initiativeBudgets map first
        if (node.initiatives?.id && initiativeBudgets.has(node.initiatives.id)) {
          const budgetData = initiativeBudgets.get(node.initiatives.id);
          if (budgetData) {
            // Convert to base currency before adding
            const nodeCurrency = budgetData.currency || baseCurrency;

            optionBreakdown.total_budget += convertAmount(
              budgetData.total_budget || 0,
              nodeCurrency,
              baseCurrency,
              exchangeRates
            );
            optionBreakdown.capex += convertAmount(
              budgetData.capex || 0,
              nodeCurrency,
              baseCurrency,
              exchangeRates
            );
            optionBreakdown.opex += convertAmount(
              budgetData.opex || 0,
              nodeCurrency,
              baseCurrency,
              exchangeRates
            );
            optionBreakdown.spend += convertAmount(
              budgetData.spend || 0,
              nodeCurrency,
              baseCurrency,
              exchangeRates
            );
            optionBreakdown.initiative_count += 1;
            return;
          }
        }

        // Otherwise use helper function
        const totalBudget = getBudgetValue(node, 'total');
        const capex = getBudgetValue(node, 'capex');
        const opex = getBudgetValue(node, 'opex');
        const spend = getBudgetValue(node, 'spend');

        optionBreakdown.total_budget += totalBudget;
        optionBreakdown.capex += capex;
        optionBreakdown.opex += opex;
        optionBreakdown.spend += spend;
        if (totalBudget > 0) {
          optionBreakdown.initiative_count += 1;
        }
      });

      // Always show all options when a filter is active (whether specific options are selected or not)
      breakdowns[filterId] = filterBreakdown;
    });

    return breakdowns;
  }, [
    activeFilterIds,
    filters,
    filteredNodes,
    visibleNodeIds,
    initiativeBudgets,
    baseCurrency,
    exchangeRates,
    reorderedFilterOptions,
  ]);

  // Fetch budget summary - disable in public view
  const { data: rawBudgetSummary, isLoading: isSummaryLoading } = useQuery(
    ['roadmap-budget-summary', roadmap.id, baseCurrency, visibleNodeIds],
    async () => {
      const result = await getRoadmapBudgetSummary({
        roadmapId: roadmap.id!,
        currency: baseCurrency,
        nodeIds: visibleNodeIds,
      });
      return result;
    },
    {
      enabled: !!roadmap.id && visibleNodeIds.length > 0 && !isPublicView,
    }
  );

  // Use calculated summary for public view, fetched summary for builder view
  const budgetSummaryToUse = isPublicView ? calculateBudgetSummaryFromNodes : rawBudgetSummary;

  // Convert budget summary to selected currency
  const budgetSummary = useMemo(() => {
    if (!budgetSummaryToUse) return undefined;
    return convertBudgetSummary(budgetSummaryToUse, baseCurrency, selectedCurrency, exchangeRates);
  }, [budgetSummaryToUse, baseCurrency, selectedCurrency, exchangeRates]);

  const displayValue = useMemo(() => {
    if (!budgetSummary) return 0;

    if (budgetViewTab === 'spend') {
      // Calculate remaining budget (total - spend)
      return budgetSummary.total_budget - budgetSummary.total_spend;
    }

    return budgetSummary.total_budget;
  }, [budgetSummary, budgetViewTab]);

  // Fetch time period breakdown - disable in public view
  const { data: rawTimePeriodBreakdown = [] } = useQuery(
    ['budget-by-time-period', roadmap.id, baseCurrency, visibleNodeIds],
    () =>
      getBudgetByTimePeriod({
        roadmapId: roadmap.id!,
        currency: baseCurrency,
        nodeIds: visibleNodeIds,
      }),
    {
      enabled: !!roadmap.id && visibleNodeIds.length > 0 && !isPublicView,
    }
  );

  // Use calculated or fetched time period breakdown
  const timePeriodBreakdownToUse = isPublicView
    ? calculateTimePeriodBreakdown
    : rawTimePeriodBreakdown;

  // Convert time period breakdown to selected currency
  const timePeriodBreakdown = useMemo(() => {
    return timePeriodBreakdownToUse.map(period =>
      convertTimePeriodBudget(period, baseCurrency, selectedCurrency, exchangeRates)
    );
  }, [timePeriodBreakdownToUse, baseCurrency, selectedCurrency, exchangeRates]);

  // Fetch section breakdown - disable in public view
  const { data: rawSectionBreakdown = [] } = useQuery(
    ['budget-by-section', roadmap.id, baseCurrency, visibleNodeIds],
    () =>
      getBudgetBySection({
        roadmapId: roadmap.id!,
        currency: baseCurrency,
        nodeIds: visibleNodeIds,
      }),
    {
      enabled: !!roadmap.id && visibleNodeIds.length > 0 && !isPublicView,
    }
  );

  // Use calculated or fetched section breakdown
  const sectionBreakdownToUse = isPublicView ? calculateSectionBreakdown : rawSectionBreakdown;

  // Convert section breakdown to selected currency
  const sectionBreakdown = useMemo(() => {
    return sectionBreakdownToUse.map(section =>
      convertSectionBudget(section, baseCurrency, selectedCurrency, exchangeRates)
    );
  }, [sectionBreakdownToUse, baseCurrency, selectedCurrency, exchangeRates]);

  const convertFilterBudget = (
    filterBudget: FilterBudget,
    fromCurrency: string,
    toCurrency: string,
    rates: Record<string, number> | undefined
  ): FilterBudget => {
    if (!rates || fromCurrency === toCurrency) return filterBudget;

    return {
      ...filterBudget,
      total_budget: convertAmount(filterBudget.total_budget, fromCurrency, toCurrency, rates),
      capex: convertAmount(filterBudget.capex, fromCurrency, toCurrency, rates),
      opex: convertAmount(filterBudget.opex, fromCurrency, toCurrency, rates),
      spend: convertAmount(filterBudget.spend, fromCurrency, toCurrency, rates),
    };
  };

  // Convert filter breakdowns to selected currency
  const filterBreakdowns = useMemo(() => {
    const convertedBreakdowns: Record<number, FilterBudget[]> = {};

    Object.entries(calculateFilterBreakdown).forEach(([filterId, breakdowns]) => {
      convertedBreakdowns[parseInt(filterId)] = breakdowns.map(fb =>
        convertFilterBudget(fb, baseCurrency, selectedCurrency, exchangeRates)
      );
    });

    return convertedBreakdowns;
  }, [calculateFilterBreakdown, baseCurrency, selectedCurrency, exchangeRates]);

  if (visibleNodeIds.length === 0) {
    return <EmptyState>{t('editor.sidebar.initiative.budget.no_initiatives_selected')}</EmptyState>;
  }

  return (
    <Container>
      {/* Grand Total Section */}
      <GrandTotalSection>
        <GrandTotalValue>
          {isSummaryLoading && !isPublicView ? (
            ''
          ) : (
            <>
              {displayValue < 0 && <NegativeSign>-</NegativeSign>}
              <CurrencySymbol>
                {formatCurrencyParts(displayValue, selectedCurrency).symbol}
              </CurrencySymbol>
              <Amount>{formatCurrencyParts(displayValue, selectedCurrency).amount}</Amount>
            </>
          )}
        </GrandTotalValue>
      </GrandTotalSection>
      {/* Total Budget Label and Currency Selector on same line */}
      <HeaderRow>
        <GrandTotalLabel>
          {budgetViewTab === 'spend'
            ? t('editor.sidebar.initiative.budget.total_budget_remaining')
            : t('editor.sidebar.initiative.budget.total_budget')}
        </GrandTotalLabel>
        <CurrencySelector
          value={selectedCurrency}
          onChange={setSelectedCurrency}
          exchangeRates={exchangeRates}
        />
      </HeaderRow>

      {budgetViewTab === 'total' && (
        <TotalBudgetView
          summary={budgetSummary}
          timePeriodBreakdown={timePeriodBreakdown}
          sectionBreakdown={sectionBreakdown}
          filterBreakdowns={filterBreakdowns}
          currency={selectedCurrency}
          isLoading={isSummaryLoading && !isPublicView}
          breakdownView={breakdownView}
          onBreakdownChange={setBreakdownView}
        />
      )}

      {budgetViewTab === 'capex-opex' && (
        <CapexOpexView
          summary={budgetSummary}
          timePeriodBreakdown={timePeriodBreakdown}
          sectionBreakdown={sectionBreakdown}
          filterBreakdowns={filterBreakdowns}
          currency={selectedCurrency}
          isLoading={isSummaryLoading && !isPublicView}
          breakdownView={breakdownView}
          onBreakdownChange={setBreakdownView}
        />
      )}

      {budgetViewTab === 'spend' && (
        <SpendView
          summary={budgetSummary}
          timePeriodBreakdown={timePeriodBreakdown}
          sectionBreakdown={sectionBreakdown}
          filterBreakdowns={filterBreakdowns}
          currency={selectedCurrency}
          isLoading={isSummaryLoading && !isPublicView}
          breakdownView={breakdownView}
          onBreakdownChange={setBreakdownView}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const GrandTotalSection = styled.div`
  text-align: center;
  margin-bottom: -0.5rem;
`;

const GrandTotalValue = styled.div`
  font-weight: 700;
  color: ${({ theme }) => theme.palette.brand.editorTabPrimary};
  display: flex;
  align-items: baseline;
  justify-content: center;
`;

const CurrencySymbol = styled.span`
  font-size: 2.75rem;
  font-weight: 600;
`;

const Amount = styled.span`
  font-size: 3.75rem;
  font-weight: 700;
`;

const NegativeSign = styled.span`
  font-size: 3.75rem;
  font-weight: 700;
`;

const HeaderRow = styled.div`
  padding: 0 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const GrandTotalLabel = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.brand.editorTabSecondary};
  letter-spacing: 0.5px;
`;

const EmptyState = styled.div`
  padding: 2rem;
  text-align: center;
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: 1rem;
`;
